import { defineStore } from 'pinia'
import crypto from 'crypto-js'
import { Str } from 'laravel-js-str'
import api from '../api'
import { useSystemStore } from './system'
import * as Sentry from '@sentry/vue'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    authUser: null
  }),
  getters: {
    authInitialized() {
      return this.authUser !== null
    }
  },
  actions: {
    async initAuth() {
      const systemStore = useSystemStore()
      await this.getAuthUser()
      await systemStore.bootSystem()
    },
    async getAuthUser() {
      const systemStore = useSystemStore()
      const response = await api.get('v3/users/me', {
        params: {
          with_admin_access_permissions_list: true
        }
      })
      this.authUser = response.data

      Sentry.setUser({
        id: this.authUser.id,
        email: this.authUser.email,
        username: this.authUser.name
      })

      systemStore.toggleDarkMode(response.data.dark_mode)
      systemStore.toggleUiAnimations(response.data.reduced_motion)
    },
    async initLogin(direct = false) {
      localStorage.state = Str.random(40)
      localStorage.codeVerifier = Str.random(128)
      localStorage.intended =
        window.location.pathname !== '/oauth/login'
          ? `${window.location.pathname}${window.location.search}`
          : window.location.search

      const host = import.meta.env.VITE_API_HOST
      const query = new URLSearchParams({
        client_id: import.meta.env.VITE_OAUTH_CLIENT_ID,
        redirect_uri: `${window.location.protocol}//${window.location.host}/oauth/login`,
        response_type: 'code',
        scope: 'global',
        state: localStorage.state,
        code_challenge: crypto
          .SHA256(localStorage.codeVerifier)
          .toString(crypto.enc.Base64)
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=/g, ''),
        code_challenge_method: 'S256'
      })

      if (direct) {
        window.location.href = `${host}/oauth/authorize?${query}`
        return
      }

      const query2 = new URLSearchParams({
        r: `/oauth/authorize?${query}`
      })

      window.location.href = `${host}/login?${query2}`
    },
    async getToken(state, code) {
      const localState = localStorage.state
      const codeVerifier = localStorage.codeVerifier
      localStorage.removeItem('state')
      localStorage.removeItem('codeVerifier')
      if (localState !== state) {
        return Promise.reject('Unexpected state')
      }

      const host = import.meta.env.VITE_API_HOST

      const response = await api.post(`${host}/oauth/token`, {
        grant_type: 'authorization_code',
        client_id: import.meta.env.VITE_OAUTH_CLIENT_ID,
        redirect_uri: `${window.location.protocol}//${window.location.host}/oauth/login`,
        code_verifier: codeVerifier,
        code: code
      })

      localStorage.accessToken = response.data.access_token
      localStorage.refreshToken = response.data.refresh_token

      return Promise.resolve(response)
    },
    logOut() {
      localStorage.clear()
      this.initLogin()
    }
  }
})
